/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { OrderListItemCreateRequest } from '../models/order-list-item-create-request';
import { OrderListItemCreateResponse } from '../models/order-list-item-create-response';
import { OrderListItemUpdateRequest } from '../models/order-list-item-update-request';
import { OrderListItemsDeleteRequest } from '../models/order-list-items-delete-request';
import { PageOrderListItemResponse } from '../models/page-order-list-item-response';

/**
 * OrderList resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderListsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateCurrentOrderListItemQuantity
   */
  static readonly UpdateCurrentOrderListItemQuantityPath =
    '/order-lists/current/items/{itemId}';

  /**
   * Updates current order list item's quantity.
   *
   * Updates an existing item based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCurrentOrderListItemQuantity()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCurrentOrderListItemQuantity$Response(
    params: {
      itemId: string;
      body: OrderListItemUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OrderListsService.UpdateCurrentOrderListItemQuantityPath,
      'put'
    );
    if (params) {
      rb.path('itemId', params.itemId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates current order list item's quantity.
   *
   * Updates an existing item based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCurrentOrderListItemQuantity$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCurrentOrderListItemQuantity(
    params: {
      itemId: string;
      body: OrderListItemUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateCurrentOrderListItemQuantity$Response(
      params,
      context
    ).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Path part for operation deleteItemFormCurrentOrderList
   */
  static readonly DeleteItemFormCurrentOrderListPath =
    '/order-lists/current/items/{itemId}';

  /**
   * Delete item from the current order list.
   *
   * Delete specified item.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteItemFormCurrentOrderList()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteItemFormCurrentOrderList$Response(
    params: {
      itemId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OrderListsService.DeleteItemFormCurrentOrderListPath,
      'delete'
    );
    if (params) {
      rb.path('itemId', params.itemId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete item from the current order list.
   *
   * Delete specified item.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteItemFormCurrentOrderList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteItemFormCurrentOrderList(
    params: {
      itemId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteItemFormCurrentOrderList$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateOrderListItemsQuantityBulk
   */
  static readonly UpdateOrderListItemsQuantityBulkPath =
    '/order-lists/current/items/bulk';

  /**
   * Updates quantities for current order list items in bulk.
   *
   * Items are deleted where quantity is 0.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateOrderListItemsQuantityBulk()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateOrderListItemsQuantityBulk$Response(
    params: {
      body: {
        [key: string]: number;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OrderListsService.UpdateOrderListItemsQuantityBulkPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates quantities for current order list items in bulk.
   *
   * Items are deleted where quantity is 0.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateOrderListItemsQuantityBulk$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateOrderListItemsQuantityBulk(
    params: {
      body: {
        [key: string]: number;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateOrderListItemsQuantityBulk$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getCurrentOrderListItems
   */
  static readonly GetCurrentOrderListItemsPath = '/order-lists/current/items';

  /**
   * Gets items of the current order list.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCurrentOrderListItems()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCurrentOrderListItems$Response(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageOrderListItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OrderListsService.GetCurrentOrderListItemsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageOrderListItemResponse>;
        })
      );
  }

  /**
   * Gets items of the current order list.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCurrentOrderListItems$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCurrentOrderListItems(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageOrderListItemResponse> {
    return this.getCurrentOrderListItems$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageOrderListItemResponse>) =>
          r.body as PageOrderListItemResponse
      )
    );
  }

  /**
   * Path part for operation addNewItemsToCurrentOrderList
   */
  static readonly AddNewItemsToCurrentOrderListPath =
    '/order-lists/current/items';

  /**
   * Add items to the current order list.
   *
   * Creates a new items and add it to the current order list.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addNewItemsToCurrentOrderList()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addNewItemsToCurrentOrderList$Response(
    params: {
      body: OrderListItemCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<OrderListItemCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OrderListsService.AddNewItemsToCurrentOrderListPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<OrderListItemCreateResponse>;
        })
      );
  }

  /**
   * Add items to the current order list.
   *
   * Creates a new items and add it to the current order list.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addNewItemsToCurrentOrderList$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addNewItemsToCurrentOrderList(
    params: {
      body: OrderListItemCreateRequest;
    },
    context?: HttpContext
  ): Observable<OrderListItemCreateResponse> {
    return this.addNewItemsToCurrentOrderList$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<OrderListItemCreateResponse>) =>
          r.body as OrderListItemCreateResponse
      )
    );
  }

  /**
   * Path part for operation deleteItemsFormCurrentOrderList
   */
  static readonly DeleteItemsFormCurrentOrderListPath =
    '/order-lists/current/items';

  /**
   * Delete items from the current order list.
   *
   * Delete specified items.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteItemsFormCurrentOrderList()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteItemsFormCurrentOrderList$Response(
    params: {
      body: OrderListItemsDeleteRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      OrderListsService.DeleteItemsFormCurrentOrderListPath,
      'delete'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete items from the current order list.
   *
   * Delete specified items.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteItemsFormCurrentOrderList$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteItemsFormCurrentOrderList(
    params: {
      body: OrderListItemsDeleteRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteItemsFormCurrentOrderList$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }
}
